import _environment from "../_environment/_environment";
import queryString from "query-string";

export enum Role {
  ADMIN = "admin",
  PRODUCER = "producer",
  BRAND = "brand",
}

export const getBriefRole = (role: Role): string => {
  switch (role) {
    case Role.ADMIN:
      return "A";
    case Role.PRODUCER:
      return "P";
    case Role.BRAND:
      return "B";
  }
}

/**
 * Slugify text
 *
 * @param text
 */
export const slugify = (text: string) => {
  return text
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "");
};

export const storeTemporaryValues = (key: string, value: any) => {
  let _: any = {};
  try {
    _ = JSON.parse(localStorage.getItem("signUpValues") as string);

    if (!_) {
      _ = {};
    }
  } catch (err) {
    _ = {};
  }

  _[key] = value;

  localStorage.setItem("signUpValues", JSON.stringify(_));

  console.log("set", _);
};

export const getTemporaryValues = () => {
  let values: any = {};

  try {
    values = JSON.parse(localStorage.getItem("signUpValues") as string);
  } catch (err) {}

  return values;
};

export const getTemporaryValue = (
  key: string,
  fallBackValue: string | null = null
) => {
  let value: any = fallBackValue;
  try {
    value = JSON.parse(localStorage.getItem("signUpValues") as string)[key];
  } catch (err) {}

  return value;
};

/**
 * What the receiving app needs in order to decide where the user lands. These
 * used to be read back out of the base64 blob; they are now passed explicitly,
 * because the handle is opaque by design and there is nothing to decode.
 */
export type RedirectAuth = {
  handle?: string | null;
  role?: string;
  is_fully_allowed?: boolean;
  version?: number;
};

/**
 * Where `back_to` is allowed to send a user, and where it sends them otherwise.
 */
const DEFAULT_BACK_TO = `${_environment.webAppURL}login`;

/** origin + path of a parsed URL: the part of a redirect target we match on. */
const targetOf = (url: URL) => `${url.origin}${url.pathname}`;

/**
 * Every redirect target `back_to` may name, as origin + path, for the running
 * environment.
 *
 * Just the webapp's login page. It is the only thing that sends users here with
 * a back_to (it passes its own /login URL), and with onboarding the only app
 * that can exchange a handle - a redirect anywhere else would deliver a handle
 * nothing consumes. Derived from the environment config, so each environment
 * allows its own webapp and nothing else.
 *
 * To add a target, add its EXACT origin + path here for every environment.
 * Never a whole domain, a prefix or a pattern - see resolveBackTo. Prefer
 * pointing a link at the webapp's /login with a returnUrl instead, which needs
 * no new entry at all.
 */
const ALLOWED_BACK_TO: string[] = [DEFAULT_BACK_TO].flatMap((entry) => {
  try {
    return [targetOf(new URL(entry))];
  } catch (err) {
    // A malformed entry must not take the sign-in page down at import time;
    // it simply allows nothing.
    console.error("auth", "invalid back_to allowlist entry", entry);
    return [];
  }
});

/** A redirect target split the way redirectWithAuthCode takes it. */
export type BackToTarget = { path: string; search: string };

/**
 * Turn a raw `back_to` query value into a target that is safe to redirect to.
 *
 * `back_to` used to go straight into `new URL()` and then into the redirect,
 * and `new URL()` only checks that a string IS a URL, not where it points - so
 * `?back_to=https://attacker.example/` sent the user there with a freshly
 * minted single-use handle attached. That was the remaining practical way to
 * steal a handle within its minute.
 *
 * Matching is EXACT on origin + path, not on the host and not on a prefix:
 * RFC 9700 requires exact matching of redirect URIs because pattern matching
 * keeps being got wrong. A host check lets `http://` through on a host that
 * should be https-only; a prefix check lets
 * `https://app.italian-artisan.com.attacker.example/` through. Comparing the
 * parsed origin also covers scheme and port, and rejects `javascript:` and
 * `data:` URLs, whose origin is "null".
 *
 * The query string is carried over (it holds the webapp's returnUrl), minus
 * any `handle` or `authCode` already in it: a fresh handle is added on the way
 * out, and a stale credential must not be forwarded.
 *
 * Anything else - missing, repeated (`?back_to=a&back_to=b` parses to an
 * array), unparseable, carrying credentials, or not on the list - falls back to
 * the webapp's login page, which is also what a missing back_to always did. The
 * user lands somewhere sensible instead of an error, and an attacker gets
 * nothing.
 */
export const resolveBackTo = (raw: unknown): BackToTarget => {
  const fallback: BackToTarget = { path: DEFAULT_BACK_TO, search: "" };

  if (raw === undefined || raw === null || raw === "") {
    return fallback;
  }

  // Logs origin + path only, never the query: whatever is in it, it is not ours
  // to write to the console or to Sentry.
  const reject = (reason: string, target?: string) => {
    console.warn("auth", `back_to rejected (${reason}); using the default`, target ?? "");
    return fallback;
  };

  if (typeof raw !== "string") {
    return reject("not a single value");
  }

  let url: URL;
  try {
    url = new URL(raw);
  } catch (err) {
    return reject("not a URL");
  }

  if (url.username || url.password) {
    return reject("carries credentials", targetOf(url));
  }

  const target = targetOf(url);
  if (!ALLOWED_BACK_TO.includes(target)) {
    return reject("not on the allowlist", target);
  }

  url.searchParams.delete("handle");
  url.searchParams.delete("authCode");

  return { path: target, search: url.search };
};

/**
 * Send the user on to `url` with a single-use handle in the query string.
 *
 * Only the handle travels. It previously carried `authCode` - the whole login
 * response in base64 - which put a live Firebase custom token into browser
 * history, the Referer header, the hosting access logs and the Smartlook
 * recordings. The handle is worthless once exchanged, and expires in a minute.
 *
 * There is deliberately no localStorage fallback any more: a single-use handle
 * cannot be replayed from storage, and reading a stale one back would only ever
 * produce a failed exchange. An already-signed-in user is recognised through
 * Firebase's own persisted session instead.
 */
export const redirectWithAuthCode = (
  url: string,
  auth: RedirectAuth,
  params?: string
) => {
  let queryParams;

  if (!!params) {
    queryParams = queryString.parse(params);
  }

  queryParams = { ...queryParams, handle: auth.handle };

  let finalUrl = url;

  if (shouldOnboard(auth)) {
    finalUrl = _environment.onboardingUrl;
  }

  window.location.href = `${finalUrl}?${queryString.stringify(queryParams)}`;
};

/**
 * A version-2 brand that has not finished registering goes to onboarding rather
 * than the app. Reads the fields off the login response directly - it no longer
 * decodes anything.
 */
export const shouldOnboard = (auth: RedirectAuth) => {
  return auth.role === 'B' && !auth.is_fully_allowed && auth.version === 2;
}
